import { useEffect, useState } from "react";
import { Form, Offcanvas } from "react-bootstrap";
import { DashSquareFill, PlusSquareFill } from "react-bootstrap-icons";
import { useForm } from "react-hook-form";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import CustomButton from "../../components/CustomButton/CustomButton";

interface AddMembersProp {
  show: boolean;
  handleClose: () => void;
  data?: any;
  member?: any;
  mode?: "add" | "edit";
}

type FormData = {
  type: string;
};

const typeOptions = [
  { label: "Member", value: "MEMBER" },
  { label: "International Partner", value: "INT_PARTNERS" },
  { label: "Knowledge Partner", value: "KNOWLEDGE_PARTNERS" },
];

const AddMembers = ({
  show,
  handleClose,
  data,
  member,
  mode = "add",
}: AddMembersProp) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>();

  const [members, setMembers] = useState<any[]>([
    { logo: "", type: "MEMBER" },
  ]);

  const [isLoading, setLoading] = useState(false);
  const [fileError, setFileError] = useState<string>("");

  // 🔁 Reset on open
  useEffect(() => {
    if (mode === "edit" && member) {
      reset({ type: member.type });
      setMembers([
        {
          logo: member.logo,
          type: member.type,
        },
      ]);
    } else {
      reset({ type: "MEMBER" });
      setMembers([{ logo: "", type: "MEMBER" }]);
    }
  }, [show, mode, member, reset]);
  const ALLOWED_IMAGE_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/svg+xml",
  ];
  // 📤 Upload logo
  const uploadFile = (file: File, index: number) => {
    const formData = new FormData();
    formData.append("file", file);
     formData.append("storage", "COUNCIL");
    WebService.fileUploadAPI({
      action: "upload",
      body: formData,
    }).then((res: any) => {
      members[index].logo = res.data?.url;
      setMembers([...members]);
    });
  };

  const onSubmit = (form: FormData) => {
    if (!data?._id) {
      toast.error("Please save Info first");
      return;
    }

    setLoading(true);

    let updatedMembers = [...(data.members || [])];

    if (mode === "add") {
      members.forEach((m) => {
        updatedMembers.push({
          ...m,
          type: form.type,
        });
      });
    }

    if (mode === "edit" && member?._id) {
      updatedMembers = updatedMembers.map((m: any) =>
        m._id === member._id
          ? {
              ...m,
              logo: members[0].logo,
              type: m.type, // ❗ type SAME rahega
            }
          : m
      );
    }

    const body = new FormData();
    body.append("members", JSON.stringify(updatedMembers));
    body.append("id", data._id);

    WebService.fileUploadAPI({
      action: "council/addMembers",
      body,
    })
      .then(() => {
        toast.success("Member saved");
        setLoading(false);
        handleClose();
      })
      .catch(() => setLoading(false));
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {mode === "add" ? "Add Member" : "Edit Member"}
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <Form>
          {/* TYPE */}
          <Form.Group className="mb-2">
            <Form.Label className="label">Type</Form.Label>
            <Form.Select
              {...register("type", { required: "Type is required" })}
              disabled={mode === "edit"}
            >
              <option value="">Select Type</option>
              {typeOptions.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Form.Select>
            {errors.type && (
              <div className="error-message">{errors.type.message}</div>
            )}
          </Form.Group>

          {/* MEMBERS */}
          <Form.Group className="mb-2">
            <Form.Label className="label">Members</Form.Label>
            <p
              className="text-primary mt-1"
              style={{ fontSize: "13px" }}
            >
              Note: Please upload an image of size: <strong>173 × 46 px</strong>{" "}
              in <strong>.png, .jpeg, or .jpg or .svg</strong> format.
            </p>
            {members.map((item, index) => (
              <div key={index} className="mb-4">
                <Form.Control
                  type="file"
                  accept="image/*"
                                             onChange={(e) => {
                              const input = e.target as HTMLInputElement;
                              const file = input.files?.[0];

                              if (!file) return;

                              if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                                const msg =
                                  "Image format not supported. Please upload PNG, JPG, JPEG or SVG image.";

                                setFileError(msg);
                                toast.error(msg);
                                input.value = "";
                                return;
                              }

                              if (file) {
                                uploadFile(file, index);
                              }
                              setFileError("");
                            }}
                />

                {item.logo && (
                  <img src={item.logo} width={80} className="mt-2" />
                )}


                {/* <Form.Control
                  type="text"
                  placeholder="Name"
                  className="mt-2"
                  value={item.name}
                  onChange={(e) => {
                    members[index].name = e.target.value;
                    setMembers([...members]);
                  }}
                /> */}

                <div className="d-flex mt-2">
                  {mode !== "edit" && (
                    <PlusSquareFill
                      className="cursor-pointer"
                      onClick={() =>
                        setMembers([
                          ...members,
                          {
                            logo: "",
                            type: watch("type") || "MEMBER",
                          },
                        ])
                      }
                    />
                  )}
                  {members.length > 1 && (
                    <DashSquareFill
                      className="cursor-pointer ms-2"
                      onClick={() =>
                        setMembers(members.filter((_, i) => i !== index))
                      }
                    />
                  )}
                </div>
              </div>
            ))}
          </Form.Group>
        </Form>
      </Offcanvas.Body>

      <div className="p-3">
        <CustomButton
          label="Save"
          className="w-100"
          isLoading={isLoading}
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </Offcanvas>
  );
};

export default AddMembers;
